import React from "react";
import styled from "styled-components";
import { FileDataDefinition } from "../../../../rule-engine-core/Common/Data/Definitions/FileDataDefinition";

import { DataEditControl } from "../../../../CommonUI/DataEditing/DataEditControl";
import { DropdownOpenIcon } from "../../../../CommonUI/Icon/DropdownOpenIcon";

import { ObserveMultiState } from "../../../../CommonUI/StateManagement/ObserveMultiState";
import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { StyledEditButton } from "../../../DesignUI/EditButton";
import { ValueRuleCell } from "../../../../rule-engine-core/Rule/Table/Value/Structure/ValueRuleCell";
import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { ValueTableRuleEditContext } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableRuleEditContext";
import { ValueTableRuleCellSetting } from "./ValueTableRuleCellSetting";
import { Error } from "../../CommonUI/Error";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";
import { BaseTestResultData } from "../../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { ValueTableRuleColumnTestResult } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableTestResult";
import { AttentionIcon } from "../../../../CommonUI/Icon/AttentionIcon";
import { Tooltip } from "@mui/material";
import { DataUtilities } from "../../../../rule-engine-core/Common/DataUtilities";
import { CellTestResultError, CellValueLine, CellValuesRenderInTester } from "../../../DesignUI/CommonStyles";
import { TestingConstants } from "../../../../CommonUI/Testing/TestingConstants";
import { ITestResultItem } from "../../../../rule-engine-core/Common/Testing/ITestResultItem";
import { UIStyleConstants } from "../../../../rule-engine-core/Common/UIStyleConstants";

interface IValueTableRuleCell {
    editContext: ValueTableRuleEditContext;
    cell: ValueRuleCell;
    ruleDefinition: ValueTableConfiguration;
    rowIndex: number;
    cellIndex: number;
}

export const XValueTableRuleCell = (props: IValueTableRuleCell) => {
    const { editContext, cell, ruleDefinition, rowIndex, cellIndex } = props;
    const testContext = React.useContext(TestContext);
    const ruleColumn = ruleDefinition.getColumnById(cell._columnId)!;
    const row = ruleDefinition.rows[rowIndex];
    const rowTestResult = testContext?.findItem(row._id) as BaseTestResultData;
    const cellTestResult = rowTestResult?.findItem(`${row._id}-${cell._columnId}`) as
        | ValueTableRuleColumnTestResult
        | undefined;

    const onSettings = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        editContext.selectedCellForSettings = cell;
    };

    const onSelect = () => {
        if (testContext) return;
        editContext.selectedCell = cell;
    };

    if (!ruleColumn) return null;

    const renderCell = () => {
        let selected = editContext.selectedCell === cell;

        function getValueStr(value: any) {
            if (typeof value === "object") {
                if (value.file && value.file instanceof File) {
                    return value.file.name;
                }

                return DataUtilities.getObjectDisplayText(value);
            }
            return value.toString();
        }

        if (cell.values !== ValueRuleCell.IGNORE_CELL_VALUE) {
            if (testContext) {
                if (Array.isArray(cell.values)) {
                    return (
                        <CellValuesRenderInTester isError={cellTestResult?.isError}>
                            {cell.values.map((v, index) => {
                                let currentItem: ITestResultItem | undefined;

                                if (cellTestResult && cellTestResult.items[index]) {
                                    currentItem = cellTestResult.items[index];
                                }

                                return (
                                    <CellValueLine
                                        key={index}
                                        testResult={currentItem?.result as "true" | "false"}
                                    >
                                        {getValueStr(v)}
                                    </CellValueLine>
                                );
                            })}
                            {cellTestResult?.isError && (
                                <CellTestResultError data-testid="ValueRuleCell-error">
                                    <Tooltip title={cellTestResult?.result || ""}>
                                        <AttentionIcon />
                                    </Tooltip>
                                </CellTestResultError>
                            )}
                        </CellValuesRenderInTester>
                    );
                } else {
                    return (
                        <CellValuesRenderInTester isError={cellTestResult?.isError}>
                            {cell.values !== ValueRuleCell.MISSING_CELL_VALUE && (
                                <CellValueLine testResult={cellTestResult?.result as "true" | "false"}>
                                    {getValueStr(cell.values)}
                                </CellValueLine>
                            )}

                            {cellTestResult?.isError && (
                                <Tooltip title={cellTestResult?.result || ""}>
                                    <CellTestResultError data-testid="ValueRuleCell-error">
                                        <AttentionIcon />
                                    </CellTestResultError>
                                </Tooltip>
                            )}
                        </CellValuesRenderInTester>
                    );
                }
            }

            return (
                <DataEditControl
                    forData={ruleDefinition.getCellDataDef(cell)}
                    value={cell.values}
                    mode={
                        ruleColumn.cellData instanceof FileDataDefinition
                            ? "display_mode_edit"
                            : selected
                              ? "edit_mode"
                              : "display_mode"
                    }
                    onUpdate={(newValue) => {
                        cell.values = newValue;
                        StateManager.propertyChanged(cell, "values");
                        StateManager.changed(ruleDefinition);
                    }}
                    navigate={editContext.navigate}
                    editMode={ruleColumn.cellData instanceof FileDataDefinition ? "layer" : "inline"}
                />
            );
        }

        return <ConstantValue>{cell.values}</ConstantValue>;
    };

    return (
        <ObserveMultiState
            listenTo={[cell, ruleColumn]}
            control={() => {
                return (
                    <ObserveState
                        listenTo={editContext}
                        properties={["selectedCell"]}
                        control={() => {
                            let selected = editContext.selectedCell === cell;
                            const validationIssues = cell.validateCellData(ruleColumn.cellData);

                            return (
                                <ValueTableRuleColumnRender
                                    onClick={onSelect}
                                    data-testid={`column_${ruleColumn._id}-${rowIndex}`}
                                    selected={selected}
                                    testResult={cellTestResult?.result as "true" | "false" | undefined}
                                    x={cellIndex + 1}
                                    y={rowIndex + 3}
                                >
                                    <Error issues={validationIssues} />

                                    {renderCell()}

                                    {!testContext && (
                                        <>
                                            <StyledEditButton
                                                onClick={onSettings}
                                                className="edit-button"
                                            >
                                                <DropdownOpenIcon />
                                            </StyledEditButton>
                                            <ValueTableRuleCellSetting
                                                cell={cell}
                                                editContext={editContext}
                                                ruleDefinition={ruleDefinition}
                                            />
                                        </>
                                    )}
                                </ValueTableRuleColumnRender>
                            );
                        }}
                    />
                );
            }}
        />
    );
};

export const ValueTableRuleColumnRender = styled.div<{
    selected: boolean;
    testResult?: "true" | "false";
    x?: number;
    y?: number;
}>`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;

    background: ${UIStyleConstants.WHITE_COLOR};
    border-radius: 2px;
    position: relative;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    ${(props) =>
        props.selected
            ? `border: 1px solid ${UIStyleConstants.BLUE_GRAY_COLOR};`
            : props.testResult === "false"
              ? `border: 1px solid ${TestingConstants.BORDER_FAIL};`
              : props.testResult === "true"
                ? `border: 1px solid ${TestingConstants.BORDER_SUCCESS};`
                : `border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};`}
    ${(props) =>
        props.x !== undefined
            ? `
                grid-column-start: ${props.x + 1};
                grid-column-end: ${props.x + 2};
                    `
            : ``}
  ${(props) =>
        props.y !== undefined
            ? `
                grid-row-start: ${props.y + 1};
                grid-row-end: ${props.y + 2};
            `
            : ``}
  .ValueEditor-root {
        width: 100%;
        padding: 6px 4px;

        .ValueEditor-display {
            padding: 6px 12px;
        }
    }

    .edit-button {
        opacity: 0;
    }

    &:hover {
        .edit-button {
            opacity: 1;
        }
    }
`;

const ConstantValue = styled.div`
    padding: 6px 12px;
`;
